import { ConfigProvider, theme as antdTheme } from "antd";
import type { Locale } from "antd/es/locale";
import dayjs from "dayjs";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Locales are imported on demand rather than statically.
 *
 * Statically importing all three put every one of them in the entry's import
 * graph just to pick one, and because antd was a manual chunk that dragged the
 * whole library onto the critical path. Until the active locale resolves,
 * ConfigProvider gets `undefined`, which is antd's English default — the same
 * thing the majority of visitors would have seen anyway.
 */
const LOCALE_LOADERS = {
  en: () => import("antd/locale/en_US"),
  ru: () => import("antd/locale/ru_RU"),
  uz: () => import("antd/locale/uz_UZ"),
} as const;

type LocaleKey = keyof typeof LOCALE_LOADERS;

const DAYJS_LOCALE_LOADERS: Record<LocaleKey, () => Promise<unknown>> = {
  en: () => Promise.resolve(),
  ru: () => import("dayjs/locale/ru"),
  uz: () => import("dayjs/locale/uz-latn"),
};

const DAYJS_LOCALE_NAME: Record<LocaleKey, string> = {
  en: "en",
  ru: "ru",
  uz: "uz-latn",
};

function normalizeLang(code: string | undefined): LocaleKey {
  const base = code?.split("-")[0]?.toLowerCase() ?? "en";
  if (base === "uz" || base === "ru") return base;
  return "en";
}

export function I18nAntdProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);
  const [antdLocale, setAntdLocale] = useState<Locale | undefined>();

  useEffect(() => {
    let cancelled = false;

    void LOCALE_LOADERS[lang]().then((module) => {
      if (!cancelled) setAntdLocale(module.default);
    });

    // dayjs locale data has to be registered before dayjs.locale() names it.
    void DAYJS_LOCALE_LOADERS[lang]().then(() => {
      if (!cancelled) dayjs.locale(DAYJS_LOCALE_NAME[lang]);
    });

    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }

    return () => {
      cancelled = true;
    };
  }, [lang]);

  return (
    <ConfigProvider
      locale={antdLocale}
      theme={{
        algorithm: antdTheme.defaultAlgorithm,
        token: {
          // Must equal --app-brand in src/theme/theme.css (antd needs a real
          // color to derive hover/active shades). See docs/STYLING.md.
          colorPrimary: "#7F56D9",
          borderRadius: 8,
        },
      }}
    >
      {children}
    </ConfigProvider>
  );
}
